import type { Maybe } from '@adornis/base/utilTypes.js';
import { cleanStringForEmailUse } from './cleanStringForEmailUser.js';

/**
 * The display name a group carries in Entra.
 *
 * School classes are prefixed with their school because their own names repeat across the whole
 * organisation ("0001b 24_25" exists in every school), and Entra has one flat list of groups.
 *
 * ! Shared between server and client on purpose. !
 * The server writes this value, the Microsoft tab compares against it. When the tab computed the name
 * differently - it compared the bare group name - every school class showed as permanently out of sync,
 * and clicking repair changed nothing because the server considered the name correct.
 * @param groupName the group's own name
 * @param parentName the name of the school the class belongs to, if it is a class
 */
export function buildGroupDisplayName(groupName: Maybe<string>, parentName?: Maybe<string>): string {
  const name = groupName ?? '';
  return parentName ? `${parentName} ${name}` : name;
}

/**
 * The mail nickname a group carries in Entra - the local part of its mail address.
 *
 * Follows the display name unless somebody set a nickname by hand, and is stripped of everything a mail
 * address cannot contain.
 * @param displayName the group's display name, see {@link buildGroupDisplayName}
 * @param manualNickname a nickname set by hand on the group, which wins if present
 */
export function buildGroupMailNickname(displayName: string, manualNickname?: Maybe<string>): string {
  return cleanStringForEmailUse(manualNickname ?? displayName) ?? '';
}
